import { useEffect, useRef, useState } from 'react';
import { Helmet } from "react-helmet-async";
import { Container } from '@mui/material';
import { TrendingUp, Restaurant, MenuBook, Brush } from '@mui/icons-material';
import { usePageTransition } from '../context/PageTransitionContext.tsx';
import FlowingMenu from '../components/flowing-menu/FlowingMenu.tsx';
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import './blog.scss';

gsap.registerPlugin(ScrollTrigger);

const blogCategories = [
  {
    text: 'Conversioni',
    icon: <TrendingUp />,
    gradient: 'linear-gradient(135deg, #7E82F7 0%, #4765FF 100%)'
  },
  {
    text: 'Settore Food',
    icon: <Restaurant />,
    gradient: 'linear-gradient(135deg, #048091 0%, #06b6a4 100%)'
  },
  {
    text: 'Guide Pratiche',
    icon: <MenuBook />,
    gradient: 'linear-gradient(135deg, #7E82F7 0%, #048091 100%)'
  },
  {
    text: 'Web Design',
    icon: <Brush />,
    gradient: 'linear-gradient(135deg, #4765FF 0%, #7E82F7 100%)'
  },
];

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  date: string;
  readTime: string;
  category: string;
  image: string;
  slug: string;
}

const blogPosts: BlogPost[] = [
  {
    id: '1',
    title: 'Accessibilità web 2025: cosa cambia con l\'European Accessibility Act',
    excerpt: 'Dichiarazione di accessibilità, documentazione tecnica e requisiti WCAG 2.1: tutto quello che devi sapere per adeguare il tuo sito alle nuove normative.',
    date: '20 Maggio 2026',
    readTime: '8 min',
    category: 'Guide Pratiche',
    image: '/sfondi/blog.png',
    slug: 'accessibilita-web-european-accessibility-act'
  },
  {
    id: '2',
    title: 'Next.js vs React: quale scegliere per il tuo progetto nel 2026',
    excerpt: 'SSR, SSG, performance e SEO a confronto. Una guida pratica per capire quando usare Next.js e quando basta React per il tuo sito web.',
    date: '10 Maggio 2026',
    readTime: '9 min',
    category: 'Web Design',
    image: '/sfondi/blog.png',
    slug: 'nextjs-vs-react-quale-scegliere'
  },
  {
    id: '3',
    title: 'SEO per piccole imprese: 10 strategie che funzionano davvero',
    excerpt: 'Dalla ricerca keyword alla local SEO: le tecniche più efficaci per far trovare la tua attività su Google senza budget pubblicitari enormi.',
    date: '28 Aprile 2026',
    readTime: '11 min',
    category: 'Guide Pratiche',
    image: '/sfondi/blog.png',
    slug: 'seo-piccole-imprese-strategie'
  },
  {
    id: '4',
    title: 'Come creare un e-commerce efficace per artigiani e piccoli negozi',
    excerpt: 'Piattaforme, costi, gestione catalogo e strategie di vendita online per chi parte da zero con il proprio shop digitale.',
    date: '15 Aprile 2026',
    readTime: '10 min',
    category: 'Conversioni',
    image: '/sfondi/blog.png',
    slug: 'ecommerce-artigiani-piccoli-negozi'
  },
  {
    id: '5',
    title: 'Landing Page Efficace: Come Convertire Visitatori in Clienti',
    excerpt: 'Gli elementi essenziali, la struttura vincente e le tecniche di copywriting per creare landing page che generano risultati concreti.',
    date: '5 Marzo 2025',
    readTime: '10 min',
    category: 'Conversioni',
    image: '/sfondi/blog.png',
    slug: 'landing-page-efficace-come-convertire'
  },
  {
    id: '6',
    title: 'Sito Web per Ristoranti: Guida Completa 2025',
    excerpt: 'Tutto quello che serve per creare un sito web efficace per il tuo ristorante: funzionalità essenziali, costi reali e strategie per aumentare le prenotazioni.',
    date: '20 Febbraio 2025',
    readTime: '9 min',
    category: 'Settore Food',
    image: '/sfondi/blog.png',
    slug: 'sito-web-per-ristoranti-guida-completa'
  },
  {
    id: '7',
    title: 'Come Scegliere un Web Designer Freelance: Guida Completa',
    excerpt: 'Criteri di valutazione, domande da fare, red flags da evitare e consigli pratici per trovare il professionista giusto.',
    date: '10 Febbraio 2025',
    readTime: '7 min',
    category: 'Guida Pratica',
    image: '/sfondi/blog.png',
    slug: 'come-scegliere-web-designer-freelance'
  },
  {
    id: '8',
    title: 'Quanto costa un sito web professionale nel 2025? Guida completa ai prezzi',
    excerpt: 'Scopri i costi reali per creare un sito web professionale: da sito vetrina a e-commerce, prezzi trasparenti e cosa include ogni servizio.',
    date: '15 Gennaio 2025',
    readTime: '8 min',
    category: 'Prezzi e Costi',
    image: '/sfondi/blog.png',
    slug: 'quanto-costa-sito-web-professionale-2025'
  },
  {
    id: '9',
    title: 'Restyling sito aziendale: quando è il momento giusto per rinnovare?',
    excerpt: 'Segnali che il tuo sito ha bisogno di un restyling, benefici del rinnovamento e come pianificare un progetto di redesign efficace.',
    date: '10 Gennaio 2025',
    readTime: '6 min',
    category: 'Web Design',
    image: '/sfondi/blog.png',
    slug: 'restyling-sito-aziendale-quando-rinnovare'
  },
  {
    id: '10',
    title: 'UI/UX Design: perché è fondamentale per il successo del tuo sito web',
    excerpt: 'Come un buon design UI/UX aumenta conversioni, migliora user experience e rende il tuo sito più competitivo. Guida pratica con esempi.',
    date: '5 Gennaio 2025',
    readTime: '7 min',
    category: 'UI/UX Design',
    image: '/sfondi/blog.png',
    slug: 'ui-ux-design-perche-fondamentale'
  }
];

const POSTS_PER_PAGE = 6;

function Blog() {
  const { navigateWithTransition } = usePageTransition();
  const heroTitleRef = useRef<HTMLHeadingElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(blogPosts.length / POSTS_PER_PAGE);
  const paginatedPosts = blogPosts.slice(
    (currentPage - 1) * POSTS_PER_PAGE,
    currentPage * POSTS_PER_PAGE
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    document.querySelector('.articles-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    // Hero title animation
    if (heroTitleRef.current) {
      const lines = heroTitleRef.current.querySelectorAll('.hero-line');
      gsap.fromTo(lines,
        { y: 100, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1.2,
          stagger: 0.15,
          ease: "power3.out",
          delay: 0.3
        }
      );
    }

    // Video parallax
    const handleScroll = () => {
      if (videoRef.current) {
        const scrolled = window.pageYOffset;
        videoRef.current.style.transform = `translateY(${scrolled * 0.5}px) scale(1.1)`;
      }
    };

    window.addEventListener('scroll', handleScroll);

    // Cards animation
    const cards = document.querySelectorAll('.article-card');
    cards.forEach((card) => {
      gsap.fromTo(card,
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: "power2.out",
          scrollTrigger: {
            trigger: card,
            start: "top 85%",
            toggleActions: "play none none none"
          }
        }
      );
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <>
      <Helmet defer={false}>
        <title>Blog Web Design | Guide Pratiche su Prezzi, SEO e UI/UX</title>
        <meta name="description" content="Quanto costa un sito web? Come scegliere un web designer? Guide pratiche con prezzi reali, consigli SEO e best practice UI/UX per PMI e professionisti." />
        <meta name="keywords" content="blog web design, tutorial web, guida seo, UI UX design, sviluppo siti web, consigli web designer, quanto costa sito web" />
        <meta property="og:title" content="Blog Web Design | Guide Pratiche su Prezzi, SEO e UI/UX" />
        <meta property="og:description" content="Guide pratiche con prezzi reali, consigli SEO e best practice UI/UX per PMI e professionisti." />
        <meta property="og:image" content="https://www.camillacinodesigndev.it/loghi/sito/preview.png" />
        <meta property="og:url" content="https://www.camillacinodesigndev.it/blog" />
        <meta property="og:locale" content="it_IT" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://www.camillacinodesigndev.it/blog" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "Blog Web Design - Camilla Cino",
            "description": "Blog dedicato a web design, UI/UX, SEO e sviluppo web",
            "url": "https://www.camillacinodesigndev.it/blog",
            "author": {
              "@type": "Person",
              "name": "Camilla Cino"
            },
            "blogPost": blogPosts.map(post => ({
              "@type": "BlogPosting",
              "headline": post.title,
              "description": post.excerpt,
              "datePublished": post.date,
              "url": `https://www.camillacinodesigndev.it/blog/${post.slug}`
            }))
          })}
        </script>
      </Helmet>

      <div className="blog-page">
        {/* Hero Section */}
        <section className="blog-hero">
          <video
            ref={videoRef}
            className="hero-video"
            autoPlay
            muted
            loop
            playsInline
            suppressHydrationWarning
          >
            <source src="/video/video2.mp4" type="video/mp4" />
          </video>
          <div className="hero-overlay"></div>
          <div className="hero-bg"></div>
          <Container maxWidth="xl" className="hero-content">
            <div className="hero-grid">
              <div className="hero-text">
                <div className="hero-label">
                  <span className="label-line"></span>
                  Insights & Guide
                </div>

                <h1 className="hero-title" ref={heroTitleRef}>
                  <span className="hero-line">Il mio</span>
                  <span className="hero-line"><span className="gradient-text">Blog</span></span>
                </h1>

                <p className="hero-description">
                  Guide pratiche, tutorial e consigli su web design, UI/UX, SEO e sviluppo web.
                  Risorse per creare siti professionali e performanti.
                </p>

                <button
                  className="hero-cta"
                  onClick={() => {
                    document.querySelector('.articles-section')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Leggi gli articoli
                  <span className="arrow">↓</span>
                </button>
              </div>

              <div className="hero-menu">
                <FlowingMenu items={blogCategories} />
              </div>
            </div>
          </Container>
        </section>

        {/* Articles Section */}
        <section className="articles-section">
          <Container maxWidth="xl">
            <div className="articles-grid">
              {paginatedPosts.map((post) => (
                <article
                  key={post.id}
                  className="article-card"
                  onClick={() => navigateWithTransition(`/blog/${post.slug}`)}
                >
                  <div className="article-image">
                    <img src={post.image} alt={post.title} />
                  </div>
                  <div className="article-content">
                    <div className="article-meta">
                      <span className="article-category">{post.category}</span>
                      <span className="article-date">{post.date} • {post.readTime}</span>
                    </div>
                    <h2 className="article-title">{post.title}</h2>
                    <p className="article-excerpt">{post.excerpt}</p>
                    <span className="article-link">
                      Leggi l'articolo
                      <span className="arrow">→</span>
                    </span>
                  </div>
                </article>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="blog-pagination">
                <button
                  className="pagination-btn"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  ←
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                ))}
                <button
                  className="pagination-btn"
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  →
                </button>
              </div>
            )}
          </Container>
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <Container maxWidth="lg">
            <h2 className="cta-title">
              Hai un progetto<br />
              <span className="glitch" data-text="ambizioso?">ambizioso?</span>
            </h2>
            <button
              className="hero-cta"
              onClick={() => navigateWithTransition('/contatti')}
            >
              Parliamone
              <span className="arrow">→</span>
            </button>
          </Container>
        </section>
      </div>
    </>
  );
}

export default Blog;
